import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import { getSessionAuthHeader, withSessionFields } from "../../../Common/sessionAuth";
import {
  BASE_URL,
  LATEST_CUSTOMER_LIST,
  NEW_CUSTOMER_TOTAL_COUNT,
} from "../../../Constants/Config";

import { ciUrl } from "../../../Constants/nodeCi";
const initialState = {
  loading: false,
  newCustomerList: [],
  totalCustomerCount: 0,
  totalRevenue: 0,
  status: false,
  error: "",
  offset: 0,
  limit: 10,
  hasMore: true,

  // OrderListing Initial state
  Orderloading: false,
  OrdernewCustomerList: [],
  Orderstatus: false,
  Ordererror: "",
  Orderoffset: 0,
  Orderlimit: 20,
  OrderhasMore: false,

  // refund Initial State
  Refundloading: false,
  RefundnewCustomerList: [],
  Refundstatus: false,
  Refunderror: "",
  Refundoffset: 0,
  Refundlimit: 20,
  RefundhasMore: false,

  /** Merge-account modal only — does not affect main customer grid (`newCustomerList`). */
  mergeModalLoading: false,
  mergeModalList: [],
  mergeModalHasMore: true,
  mergeModalError: "",
};
let activeRequests = 0;
let activeNewCustomerListRequests = 0;
let activeMergeModalListRequests = 0;
let cancelTokenSource;
let cancelTokenSource1;
let cancelTokenSource2;
let cancelTokenMergeModal;

export const fetchNewCustomerList = createAsyncThunk(
  "customerSlice/fetchNewCustomerList",
  async (data, { rejectWithValue }) => {
    if (cancelTokenSource2) {
      cancelTokenSource2.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource2 = axios.CancelToken.source();
    try {
      const { token, skipTotalCount, ...rest } = data;
      const dataNew = withSessionFields(rest);
      const page = data?.page_no ?? data?.page ?? 1;
      const requestConfig = {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : getSessionAuthHeader()),
        },
        cancelToken: cancelTokenSource2.token,
      };

      const listRequest = axios.post(
        ciUrl(LATEST_CUSTOMER_LIST),
        dataNew,
        requestConfig,
      );

      let response;
      let totalCountResponse = null;
      if (page === 1 && !skipTotalCount) {
        const totalCountRequest = axios
          .post(
            ciUrl(NEW_CUSTOMER_TOTAL_COUNT),
            dataNew,
            requestConfig,
          )
          .catch(() => null);
        [response, totalCountResponse] = await Promise.all([
          listRequest,
          totalCountRequest,
        ]);
      } else {
        response = await listRequest;
      }
      if (
        response.data.status === true &&
        Array.isArray(response?.data?.result)
      ) {
        const arr = response?.data?.result;
        const status = response.data.status;
        const totalCustomerCount = skipTotalCount
          ? response?.data?.total_count ?? 0
          : totalCountResponse?.data?.result?.total_count ??
          response?.data?.total_count ??
          0;
        const totalRevenue = skipTotalCount
          ? 0
          : totalCountResponse?.data?.result?.total_lifetime_order_amt ?? 0;
        return {
          arr,
          status,
          totalCustomerCount,
          totalRevenue,
          page,
          skipTotalCount: !!skipTotalCount,
        };
      } else if (response.data.status === false) {
        const arr = [];
        const status = false;
        return {
          arr,
          status,
          totalCustomerCount: 0,
          totalRevenue: 0,
          page,
          skipTotalCount: !!skipTotalCount,
        };
      }
    } catch (error) {
      // Handle axios cancellation errors — must reject so fulfilled does not run
      // (otherwise stale cancelled requests set loading=false / clear list while a newer request is in flight).
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }

      // Handle other errors
      const customError = {
        message: error?.message || "An error occurred",
        status: toThunkRejectValue(error).status,
        data: error?.response?.data || null,
      };
      return rejectWithValue(customError);
    }
  }
);

/** Customer list for Merge Customer modal only (same API as main list, isolated Redux state). */
export const fetchMergeModalCustomerList = createAsyncThunk(
  "customerSlice/fetchMergeModalCustomerList",
  async (data, { rejectWithValue }) => {
    if (cancelTokenMergeModal) {
      cancelTokenMergeModal.cancel("Previous merge modal request canceled");
    }
    cancelTokenMergeModal = axios.CancelToken.source();
    try {
      const { token, ...rest } = data;
      const dataNew = withSessionFields(rest);
      const page = data?.page_no ?? data?.page ?? 1;
      const response = await axios.post(
        ciUrl(LATEST_CUSTOMER_LIST),
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token ? { Authorization: `Bearer ${token}` } : getSessionAuthHeader()),
          },
          cancelToken: cancelTokenMergeModal.token,
        },
      );
      if (
        response.data.status === true &&
        Array.isArray(response?.data?.result)
      ) {
        const arr = response?.data?.result;
        const status = response.data.status;
        return { arr, status, page };
      }
      if (response.data.status === false) {
        return { arr: [], status: false, page };
      }
      return rejectWithValue({
        message: "Invalid API response",
        data: response.data,
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }
      return rejectWithValue({
        message: error?.message || "An error occurred",
        status: toThunkRejectValue(error).status,
        data: error?.response?.data || null,
      });
    }
  },
);

export function cancelMergeModalCustomerListRequests() {
  if (cancelTokenMergeModal) {
    cancelTokenMergeModal.cancel("Merge modal closed");
    cancelTokenMergeModal = null;
  }
}

export const fetchPaidOrderList = createAsyncThunk(
  "customerSlice/fetchPaidOrderList",
  async (data, { rejectWithValue, signal }) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource = axios.CancelToken.source();
    try {
      const { token, ...dataNew } = data;

      const response = await axios.post(
        BASE_URL + "Customerapi/new_mrtwise_customer_list_api_count",
        withSessionFields(dataNew),
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getSessionAuthHeader(),
          },
          // signal: signal, // Add cancellation support
          cancelToken: cancelTokenSource.token,
        }
      );

      const res = response.data;
      if (res?.status === true) {
        const arr = res.result;
        const status = res.status;
        const totalCustomerCount = res.total_count;
        return { arr, status, totalCustomerCount };
      } else if (res?.status === false) {
        return { arr: [], status: false, totalCustomerCount: 0 };
      } else {
        // ✅ Catch unexpected API shape
        return rejectWithValue({
          message: "Invalid API response structure",
          data: res,
        });
      }
    } catch (error) {
      // Handle axios cancellation errors
      if (axios.isCancel(error)) {
        // Don't reject cancelled requests, just return empty result
        return { arr: [], status: false, totalCustomerCount: 0 };
      }

      // Handle other errors
      const customError = {
        message: error?.message || "An error occurred",
        status: toThunkRejectValue(error).status,
        data: error?.response?.data || null,
      };
      return rejectWithValue(customError);
    }
  }
);

/// fetchRefundOrderList can be added here similarly

export const fetchRefundOrderList = createAsyncThunk(
  "customerSlice/fetchRefundOrderList",
  async (data, { rejectWithValue, signal }) => {
    if (cancelTokenSource1) {
      cancelTokenSource1.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource1 = axios.CancelToken.source();
    try {
      const { token, ...dataNew } = data;

      const response = await axios.post(
        BASE_URL + "Customerapi/new_mrtwise_customer_list_api_count",
        withSessionFields(dataNew),
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getSessionAuthHeader(),
          },
          cancelToken: cancelTokenSource1.token,
        }
      );

      const res = response.data;
      if (res?.status === true) {
        const arr = res.result;
        const status = res.status;
        const totalCustomerCount = res.total_count;
        return { arr, status, totalCustomerCount };
      } else if (res?.status === false) {
        return { arr: [], status: false, totalCustomerCount: 0 };
      } else {
        // ✅ Catch unexpected API shape
        return rejectWithValue({
          message: "Invalid API response structure",
          data: res,
        });
      }
    } catch (error) {
      // Handle axios cancellation errors
      if (axios.isCancel(error)) {
        // Don't reject cancelled requests, just return empty result
        return { arr: [], status: false, totalCustomerCount: 0 };
      }

      // Handle other errors
      const customError = {
        message: error?.message || "An error occurred",
        status: toThunkRejectValue(error).status,
        data: error?.response?.data || null,
      };
      return rejectWithValue(customError);
    }
  }
);

const customerSlice = createSlice({
  name: "customerSlice",
  initialState,
  reducers: {
    emptyCustomer: (state, action) => {
      // console.log("empty action payload: ", action.payload);
      state.productsData = [];
      state.offset = 0;
      state.limit = 10;
      state.hasMore = true;
      state.page = 1;
    },

    resetCustomerList: (state) => {
      state.newCustomerList = [];
      state.totalCustomerCount = 0;
      state.totalRevenue = 0;
      state.hasMore = true;
      state.offset = 0;
    },

    clearOrderState: (state, action) => {
      state.Orderloading = false;
      state.OrdernewCustomerList = [];
      state.Orderstatus = false;
      state.Ordererror = "";
      state.Orderoffset = 0;
      state.Orderlimit = 20;
      state.OrderhasMore = true;
    },

    clearRefundState: (state, action) => {
      state.Refundloading = false;
      state.RefundnewCustomerList = [];
      state.Refundstatus = false;
      state.Refunderror = "";
      state.Refundoffset = 0;
      state.Refundlimit = 20;
      state.RefundhasMore = true;
    },

    clearMergeModalCustomerList: (state) => {
      state.mergeModalLoading = false;
      state.mergeModalList = [];
      state.mergeModalHasMore = true;
      state.mergeModalError = "";
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchMergeModalCustomerList.pending, (state) => {
      activeMergeModalListRequests++;
      state.mergeModalLoading = true;
    });
    builder.addCase(fetchMergeModalCustomerList.fulfilled, (state, action) => {
      activeMergeModalListRequests = Math.max(0, activeMergeModalListRequests - 1);
      state.mergeModalLoading = activeMergeModalListRequests > 0;
      state.mergeModalError = "";
      const page = action.meta?.arg?.page_no ?? 1;
      const arr = Array.isArray(action?.payload?.arr) ? action.payload.arr : [];
      const pageSize = action.meta?.arg?.limit ?? state.limit ?? 10;
      if (page === 1) {
        state.mergeModalList = arr;
      } else {
        state.mergeModalList = [...state.mergeModalList, ...arr];
      }
      state.mergeModalHasMore = arr.length >= pageSize;
    });
    builder.addCase(fetchMergeModalCustomerList.rejected, (state, action) => {
      activeMergeModalListRequests = Math.max(0, activeMergeModalListRequests - 1);
      state.mergeModalLoading = activeMergeModalListRequests > 0;
      if (action.payload?.cancelled) {
        return;
      }
      state.mergeModalList = [];
      state.mergeModalHasMore = false;
      const errorMessage =
        action.payload?.message || action.error?.message || "An error occurred";
      state.mergeModalError =
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage);
    });

    builder.addCase(fetchNewCustomerList.pending, (state) => {
      activeNewCustomerListRequests++;
      state.loading = true;
    });
    builder.addCase(fetchNewCustomerList.fulfilled, (state, action) => {
      activeNewCustomerListRequests = Math.max(0, activeNewCustomerListRequests - 1);
      state.loading = activeNewCustomerListRequests > 0;
      state.status = action?.payload?.status;
      state.error = "";

      // Determine which page was requested from the thunk args (not payload)
      // console.log("action.meta?.arg", action.meta?.arg);
      const page = action.meta?.arg?.page_no ?? 1;
      const arr = Array.isArray(action?.payload?.arr) ? action.payload.arr : [];
      // Page size comes from the original thunk args; fall back to state.limit.
      const pageSize = action.meta?.arg?.limit ?? state.limit ?? 10;

      if (page === 1) {
        // Initial load or filter change: replace list
        state.newCustomerList = arr;
        if (!action.meta?.arg?.skipTotalCount) {
          state.totalCustomerCount = action?.payload?.totalCustomerCount ?? 0;
          state.totalRevenue = action?.payload?.totalRevenue ?? 0;
        }
      } else {
        // Subsequent pages: append
        state.newCustomerList = [...state.newCustomerList, ...arr];
      }

      // Continue loading while we receive full pages; stop when the last page
      // has fewer items than requested (including 0).
      state.hasMore = arr.length >= pageSize;
    });
    builder.addCase(fetchNewCustomerList.rejected, (state, action) => {
      activeNewCustomerListRequests = Math.max(0, activeNewCustomerListRequests - 1);
      state.loading = activeNewCustomerListRequests > 0;
      if (action.payload?.cancelled) {
        return;
      }
      state.newCustomerList = [];
      state.totalCustomerCount = 0;
      state.totalRevenue = 0;
      // When using rejectWithValue, error is in action.payload, not action.error
      const errorMessage =
        action.payload?.message || action.error?.message || "An error occurred";
      state.error =
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage);
    });

    ///// Paid Order List
    builder.addCase(fetchPaidOrderList.pending, (state) => {
      activeRequests++;
      state.Orderloading = true;
    });
    builder.addCase(fetchPaidOrderList.fulfilled, (state, action) => {
      activeRequests = Math.max(0, activeRequests - 1); // Ensure it doesn't go negative
      // Always set loading to false when paid order request completes (independent of other requests)
      state.Orderloading = false;

      if (!Array.isArray(state.OrdernewCustomerList)) {
        state.OrdernewCustomerList = [];
      }
      state.Orderstatus = action.payload?.status;

      // If status is false (no data), ensure loading is false and reset list
      if (action.payload?.status === false) {
        state.Orderloading = false;
        state.OrdernewCustomerList = [];
        state.OrderhasMore = false;
        state.Ordererror = "";
        return;
      } else {
        state.Orderloading = false;
      }

      // Check if this is initial load (offset was 0) OR if list is empty (tab switch/reset)
      const wasInitialLoad =
        state.Orderoffset === 0 || state.OrdernewCustomerList.length === 0;

      if (wasInitialLoad) {
        // Replace list on initial load or when list is empty (tab switch)
        state.OrdernewCustomerList = action.payload?.arr || [];
      } else {
        // Append to list for pagination
        state.OrdernewCustomerList = [
          ...state.OrdernewCustomerList,
          ...(action.payload?.arr || []),
        ];
      }

      const fetchedCount = action.payload?.arr?.length || 0;
      const totalCount = action.payload?.totalCustomerCount || 0;
      const totalLoaded = state.OrdernewCustomerList.length;

      // ✅ Update pagination - only increment offset if this was a pagination request
      if (fetchedCount > 0 && !wasInitialLoad) {
        state.Orderoffset += fetchedCount; // increment by actual fetched size
      } else if (wasInitialLoad && fetchedCount > 0) {
        // On initial load, set offset to fetched count for next pagination
        state.Orderoffset = fetchedCount;
      }

      // ✅ Correct hasMore logic
      state.OrderhasMore = totalLoaded < totalCount;

      state.Ordererror = "";
    });
    builder.addCase(fetchPaidOrderList.rejected, (state, action) => {
      activeRequests = Math.max(0, activeRequests - 1); // Ensure it doesn't go negative
      // Always set loading to false on rejection
      state.Orderloading = false;
      state.OrdernewCustomerList = [];
      // When using rejectWithValue, error is in action.payload, not action.error
      const errorMessage =
        action.payload?.message || action.error?.message || "An error occurred";
      state.Ordererror =
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage);
      state.OrderhasMore = false;
    });

    //// Refund Order List

    builder.addCase(fetchRefundOrderList.pending, (state) => {
      activeRequests++;
      state.Refundloading = true;
    });
    builder.addCase(fetchRefundOrderList.fulfilled, (state, action) => {
      activeRequests = Math.max(0, activeRequests - 1); // Ensure it doesn't go negative
      // Always set loading to false when refund request completes (independent of other requests)
      state.Refundloading = false;

      if (!Array.isArray(state.RefundnewCustomerList)) {
        state.RefundnewCustomerList = [];
      }
      state.Refundstatus = action.payload?.status;

      // If status is false (no data), ensure loading is false and reset list
      if (action.payload?.status === false) {
        state.Refundloading = false;
        state.RefundnewCustomerList = [];
        state.RefundhasMore = false;
        state.Refunderror = "";
        return;
      } else {
        state.Refundloading = false;
      }

      // Check if this is initial load (offset was 0) OR if list is empty (tab switch/reset)
      const wasInitialLoad =
        state.Refundoffset === 0 || state.RefundnewCustomerList.length === 0;

      if (wasInitialLoad) {
        // Replace list on initial load or when list is empty (tab switch)
        state.RefundnewCustomerList = action.payload?.arr || [];
      } else {
        // Append to list for pagination
        state.RefundnewCustomerList = [
          ...state.RefundnewCustomerList,
          ...(action.payload?.arr || []),
        ];
      }

      const fetchedCount = action.payload?.arr?.length || 0;
      const totalCount = action.payload?.totalCustomerCount || 0;
      const totalLoaded = state.RefundnewCustomerList.length;

      // ✅ Update pagination - only increment offset if this was a pagination request
      if (fetchedCount > 0 && !wasInitialLoad) {
        state.Refundoffset += fetchedCount; // increment by actual fetched size
      } else if (wasInitialLoad && fetchedCount > 0) {
        // On initial load, set offset to fetched count for next pagination
        state.Refundoffset = fetchedCount;
      }

      // ✅ Correct hasMore logic
      state.RefundhasMore = totalLoaded < totalCount;

      state.Refunderror = "";
    });
    builder.addCase(fetchRefundOrderList.rejected, (state, action) => {
      activeRequests = Math.max(0, activeRequests - 1); // Ensure it doesn't go negative
      // Always set loading to false on rejection
      state.Refundloading = false;
      state.RefundnewCustomerList = [];
      state.Refundstatus = true;
      // When using rejectWithValue, error is in action.payload, not action.error
      const errorMessage =
        action.payload?.message || action.error?.message || "An error occurred";
      state.Refunderror =
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage);
      state.RefundhasMore = false;
    });
  },
});
export const {
  emptyCustomer,
  resetCustomerList,
  clearOrderState,
  clearRefundState,
  clearMergeModalCustomerList,
} = customerSlice.actions;
export default customerSlice.reducer;
